import {
  CheckOutlined,
  PlusOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  Alert,
  AutoComplete,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ProTable,
  type ActionType,
  type ProColumns,
} from '@ant-design/pro-components';
import type { CurrentUser } from '@cell-bank/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatTenantDateTime } from '../lib/tenant';

type InventoryRequest = {
  id: string;
  requestNo: string;
  operationType: string;
  status: string;
  title: string;
  reason: string | null;
  requestedByName?: string | null;
  itemCount?: number;
  cellLineSummary?: string | null;
  materialSummary?: string | null;
  passageSummary?: string | null;
  version: number;
  createdAt: string;
  executedAt?: string | null;
};
type RequestDetail = InventoryRequest & {
  requestedBy: string;
  items: {
    id: string;
    sampleId: string;
    expectedSampleVersion: number;
    fromStatus: string | null;
    fromLocationId: string | null;
    toLocationId: string | null;
    sample: {
      sampleNo: string;
      barcode: string | null;
      cellLineCode?: string | null;
      materialName?: string | null;
      passage?: string | null;
      status: string;
      customFields?: Record<string, unknown> | null;
    };
  }[];
  actions: {
    id: string;
    action: string;
    actorId: string;
    comment: string | null;
    fromStatus: string;
    toStatus: string;
    createdAt: string;
  }[];
};
type FormValues = {
  operationType: string;
  title: string;
  reason?: string;
  sampleRefs: string;
  toLocationId?: string;
};
export type PrefillSample = {
  sampleId: string;
  sampleNo: string;
  version: number;
  status: string;
  storagePositionId: string | null;
  cellLineCode?: string;
  materialName?: string | null;
  passage?: string | null;
  storagePositionLabel?: string | null;
};
type Position = { id: string; path: string; displayPath?: string };
type ExecutionPreview = {
  executable: boolean;
  operationType: string;
  issues: { code: string; messageKey: string; sampleNo?: string }[];
  items: {
    itemId: string;
    sampleId: string;
    sampleNo: string;
    sampleLabel?: string | null;
    fromStatus: string;
    toStatus: string;
    fromLocationId: string | null;
    toLocationId: string | null;
    issues: { code: string; messageKey: string }[];
  }[];
};
type OperationSettings = Record<
  string,
  { enabled: boolean; userVisible: boolean }
>;

const requestOperationTypes = [
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
];
const defaultOperationSettings: OperationSettings = {
  PUTAWAY: { enabled: true, userVisible: true },
  CONSUME: { enabled: true, userVisible: true },
  LOAN: { enabled: false, userVisible: false },
  RETURN: { enabled: false, userVisible: false },
  MOVE: { enabled: true, userVisible: false },
  DISPOSE: { enabled: true, userVisible: false },
  QUARANTINE: { enabled: false, userVisible: false },
};

export function RequestsPage({
  approvals = false,
  embeddedSamples,
  onEmbeddedClose,
}: {
  approvals?: boolean;
  embeddedSamples?: PrefillSample[];
  onEmbeddedClose?: () => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [positions, setPositions] = useState<Position[]>([]);
  const [prefillSamples, setPrefillSamples] = useState<PrefillSample[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<RequestDetail>();
  const [execution, setExecution] = useState<{
    request: InventoryRequest;
    preview: ExecutionPreview;
    reason: string;
  }>();
  const actionRef = useRef<ActionType | undefined>(undefined);
  const [form] = Form.useForm<FormValues>();
  const sampleRefsValue = Form.useWatch('sampleRefs', form);
  const operationTypeValue = Form.useWatch('operationType', form);
  const [messageApi, contextHolder] = message.useMessage();
  const canExecuteInventory =
    currentUser?.permissions.includes('inventory:execute') ?? false;
  const availableOperations = useMemo(() => {
    const operationSettings =
      currentUser?.tenant.inventoryOperationSettings ??
      defaultOperationSettings;
    const canUseManagerOperations =
      currentUser?.permissions.includes('inventory:execute') ?? false;
    return requestOperationTypes.filter((operationType) => {
      const setting = operationSettings[operationType];
      return (
        setting?.enabled && (setting.userVisible || canUseManagerOperations)
      );
    });
  }, [currentUser]);
  const requestItems = async (params: Record<string, unknown>) => {
    const search = new URLSearchParams({
      relation: approvals ? 'ACTIONABLE' : 'MY_REQUESTS',
    });
    const status = params.status;
    if (typeof status === 'string' && status) search.set('status', status);
    const requestNo = params.requestNo;
    if (typeof requestNo === 'string' && requestNo.trim())
      search.set('requestNo', requestNo.trim());
    try {
      const response = await fetch(`/api/v1/inventory-requests?${search}`, {
        credentials: 'include',
      });
      if (response.status === 401) {
        window.location.assign('/login');
        return { data: [], success: false, total: 0 };
      }
      if (!response.ok) throw new Error(t('requests.loadFailed'));
      const payload = (await response.json()) as {
        items: InventoryRequest[];
      };
      return {
        data: payload.items,
        success: true,
        total: payload.items.length,
      };
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('requests.loadFailed'),
      );
      return { data: [], success: false, total: 0 };
    }
  };
  useEffect(() => {
    void fetch('/api/v1/auth/me', { credentials: 'include' })
      .then(async (response) => {
        if (response.ok) setCurrentUser((await response.json()) as CurrentUser);
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    void fetch('/api/v1/storage-positions', { credentials: 'include' })
      .then(async (response) => {
        if (response.ok)
          setPositions(
            ((await response.json()) as { items: Position[] }).items,
          );
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    const state = (
      embeddedSamples ? { requestSamples: embeddedSamples } : location.state
    ) as {
      createRequest?: boolean;
      operationType?: string;
      requestSamples?: PrefillSample[];
    } | null;
    if (approvals || (embeddedSamples && !currentUser)) return;
    if (state?.createRequest) {
      if (!currentUser) return;
      const operationType =
        state.operationType && availableOperations.includes(state.operationType)
          ? state.operationType
          : (availableOperations[0] ?? 'CONSUME');
      setPrefillSamples([]);
      form.resetFields();
      form.setFieldsValue({
        operationType,
        title: '',
        reason: '',
        sampleRefs: '',
      });
      setOpen(true);
      void navigate('/requests', { replace: true, state: null });
      return;
    }
    if (!state?.requestSamples?.length) return;
    const refs = state.requestSamples
      .map((sample) => `${sample.sampleId}:${sample.version}`)
      .join('\n');
    setPrefillSamples(state.requestSamples);
    const sampleSummary = Array.from(
      new Set(
        state.requestSamples
          .map((sample) =>
            [sample.cellLineCode, sample.materialName, sample.passage]
              .filter(Boolean)
              .join(' · '),
          )
          .filter(Boolean),
      ),
    ).join('、');
    form.setFieldsValue({
      operationType: availableOperations[0] ?? 'CONSUME',
      title: t('requests.defaultCheckoutTitle', {
        summary: sampleSummary || t('requests.genericSamples'),
        count: state.requestSamples.length,
      }),
      reason: '',
      sampleRefs: refs,
    });
    setOpen(true);
    if (!embeddedSamples)
      void navigate('/requests', { replace: true, state: null });
  }, [
    approvals,
    embeddedSamples,
    availableOperations,
    currentUser,
    form,
    location.state,
    navigate,
    t,
  ]);
  const call = async (
    id: string,
    action: string,
    body: Record<string, unknown>,
  ) => {
    const response = await fetch(`/api/v1/inventory-requests/${id}/${action}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(t('requests.actionFailed'));
    await actionRef.current?.reload();
  };
  const inspect = async (id: string) => {
    const response = await fetch(`/api/v1/inventory-requests/${id}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(t('requests.detailFailed'));
      return;
    }
    setSelected((await response.json()) as RequestDetail);
  };
  const previewExecution = async (row: InventoryRequest) => {
    const response = await fetch(
      `/api/v1/inventory-requests/${row.id}/execution-preview`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      await messageApi.error(t('requests.previewFailed'));
      return;
    }
    setExecution({
      request: row,
      preview: (await response.json()) as ExecutionPreview,
      reason: row.reason ?? '',
    });
  };
  const confirmExecution = async () => {
    if (!execution) return;
    try {
      await call(execution.request.id, 'execute', {
        version: execution.request.version,
        reason: execution.reason,
      });
      setExecution(undefined);
      await messageApi.success(t('requests.executed'));
    } catch {
      await messageApi.error(t('requests.actionFailed'));
    }
  };
  const operationLabel = (value: string) =>
    t(`requests.operationTypes.${value}`, { defaultValue: value });
  const sampleStatusLabel = (value: string) =>
    t(`samples.statusOptions.${value}`, { defaultValue: value });
  const requestStatusLabel = (value: string) =>
    t(`requests.statusOptions.${value}`, { defaultValue: value });
  const requestActionLabel = (value: string) =>
    t(`requests.actionTypes.${value}`, { defaultValue: value });
  const positionPath = (id: string | null | undefined) =>
    id
      ? (positions.find((position) => position.id === id)?.displayPath ??
        positions.find((position) => position.id === id)?.path ??
        id)
      : '-';
  const issueText = (issue: { code: string; messageKey: string }) =>
    t(issue.messageKey, { defaultValue: issue.code });
  const expectedStatuses = (operationType: string | undefined) =>
    operationType === 'RETURN'
      ? ['LOANED']
      : operationType === 'DISPOSE'
        ? ['AVAILABLE', 'QUARANTINED']
        : ['AVAILABLE'];
  const create = async (values: FormValues) => {
    const needsTarget = ['MOVE', 'RETURN'].includes(values.operationType);
    const sampleRefs =
      values.sampleRefs ??
      prefillSamples
        .map((sample) => `${sample.sampleId}:${sample.version}`)
        .join('\n');
    const refs = sampleRefs
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => {
        const [sampleId, version] = value.split(':').map((part) => part.trim());
        return {
          sampleId,
          expectedSampleVersion: Number(version),
          ...(needsTarget ? { toLocationId: values.toLocationId } : {}),
        };
      });
    if (
      !refs.length ||
      refs.some(
        (item) =>
          !item.sampleId ||
          !Number.isInteger(item.expectedSampleVersion) ||
          item.expectedSampleVersion < 1,
      )
    ) {
      await messageApi.error(t('requests.batchFormat'));
      return;
    }
    const response = await fetch('/api/v1/inventory-requests', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        operationType: values.operationType,
        title: values.title,
        reason: values.reason?.trim() || null,
        items: refs,
      }),
    });
    if (!response.ok) {
      await messageApi.error(t('requests.saveFailed'));
      return;
    }
    const created = (await response.json()) as InventoryRequest;
    const submitResponse = await fetch(
      `/api/v1/inventory-requests/${created.id}/submit`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ version: created.version }),
      },
    );
    setOpen(false);
    form.resetFields();
    setPrefillSamples([]);
    await actionRef.current?.reload();
    if (!submitResponse.ok) {
      await messageApi.error(t('requests.submitFailed'));
      return;
    }
    await messageApi.success(t('requests.submitted'));
    onEmbeddedClose?.();
  };
  const previewRows = useMemo(() => {
    const operationType = operationTypeValue ?? 'CONSUME';
    const expected = expectedStatuses(operationType);
    const seen = new Set<string>();
    const refs = (
      prefillSamples.length
        ? prefillSamples
            .map((sample) => `${sample.sampleId}:${sample.version}`)
            .join('\n')
        : (sampleRefsValue ?? '')
    )
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
    return refs.map((ref, index) => {
      const [sampleId = '', version = ''] = ref
        .split(':')
        .map((part) => part.trim());
      const match = prefillSamples.find(
        (sample) => sample.sampleId === sampleId,
      );
      const issues: string[] = [];
      if (sampleId && seen.has(sampleId))
        issues.push(t('requests.localIssue.duplicateSample'));
      if (sampleId) seen.add(sampleId);
      if (match && !expected.includes(match.status)) {
        issues.push(
          t('requests.localIssue.statusMismatch', {
            expected: expected.join(' / '),
          }),
        );
      }
      return {
        key: `${sampleId}-${index}`,
        sampleId,
        sampleNo: match?.sampleNo ?? sampleId,
        version,
        status: match?.status ?? '-',
        cellLine: match?.cellLineCode ?? '-',
        material: match?.materialName ?? '-',
        passage: match?.passage ?? '-',
        expectedStatus: expected.join(' / '),
        storagePositionId: match?.storagePositionId ?? '-',
        storagePosition:
          match?.storagePositionLabel ?? positionPath(match?.storagePositionId),
        issues,
      };
    });
  }, [operationTypeValue, prefillSamples, sampleRefsValue, t]);
  const previewBlockerCount = previewRows.reduce(
    (count, row) => count + row.issues.length,
    0,
  );
  const columns: ProColumns<InventoryRequest>[] = [
    {
      title: t('requests.content'),
      key: 'content',
      width: 220,
      ellipsis: true,
      search: false,
      render: (_, row) =>
        [row.cellLineSummary, row.materialSummary, row.passageSummary]
          .filter(Boolean)
          .join(' · ') || '-',
    },

    {
      title: t('requests.operation'),
      dataIndex: 'operationType',
      search: false,
      width: 90,
      render: (_, row) => operationLabel(row.operationType),
    },
    {
      title: t('requests.applicant'),
      dataIndex: 'requestedByName',
      width: 120,
      ellipsis: true,
      search: false,
      render: (_, row) => row.requestedByName ?? '-',
    },

    {
      title: t('requests.itemCount'),
      dataIndex: 'itemCount',
      width: 90,
      search: false,
      render: (_, row) => row.itemCount ?? 0,
    },

    {
      title: t('requests.status'),
      dataIndex: 'status',
      width: 90,
      valueType: 'select',
      valueEnum: Object.fromEntries(
        [
          'DRAFT',
          'PENDING',
          'WITHDRAWN',
          'REJECTED',
          'APPROVED',
          'EXECUTED',
          'CANCELLED',
        ].map((value) => [value, { text: requestStatusLabel(value) }]),
      ),
      render: (_, row) => (
        <Tag
          color={
            row.status === 'PENDING'
              ? 'gold'
              : row.status === 'EXECUTED'
                ? 'green'
                : 'blue'
          }
        >
          {requestStatusLabel(row.status)}
        </Tag>
      ),
    },
    { title: t('requests.number'), dataIndex: 'requestNo', width: 160 },
    {
      title: t('requests.title'),
      dataIndex: 'title',
      width: 200,
      ellipsis: true,
      search: false,
    },
    {
      title: t('requests.createdAt'),
      dataIndex: 'createdAt',
      width: 160,
      search: false,
      render: (_, row) => formatTenantDateTime(row.createdAt),
    },
    {
      title: t('requests.actions'),
      key: 'actions',
      fixed: 'right',
      width: approvals ? 220 : 160,
      search: false,
      render: (_, row) => (
        <Space>
          <Button size="small" type="link" onClick={() => void inspect(row.id)}>
            {t('requests.detail')}
          </Button>
          {approvals && row.status === 'PENDING' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() =>
                  void call(row.id, 'approve', { version: row.version })
                }
              >
                {t('requests.approve')}
              </Button>
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() =>
                  void call(row.id, 'reject', {
                    version: row.version,
                    comment: t('requests.defaultReject'),
                  })
                }
              >
                {t('requests.reject')}
              </Button>
            </>
          )}
          {canExecuteInventory && row.status === 'APPROVED' && (
            <>
              <Button
                size="small"
                type="primary"
                onClick={() => void previewExecution(row)}
              >
                {t('requests.execute')}
              </Button>
              <Button
                size="small"
                danger
                onClick={() => {
                  const comment = window.prompt(t('requests.cancelPrompt'));
                  if (comment?.trim())
                    void call(row.id, 'cancel', {
                      version: row.version,
                      comment,
                    });
                }}
              >
                {t('requests.cancel')}
              </Button>
            </>
          )}
          {!approvals && row.status === 'PENDING' && (
            <Button
              size="small"
              danger
              onClick={() =>
                void call(row.id, 'withdraw', { version: row.version })
              }
            >
              {t('requests.withdraw')}
            </Button>
          )}
        </Space>
      ),
    },
  ];
  const tableWidth = columns.reduce(
    (total, column) =>
      total + (typeof column.width === 'number' ? column.width : 0),
    0,
  );
  return (
    <section>
      {contextHolder}
      {!embeddedSamples && (
        <ProTable<InventoryRequest>
          rowKey="id"
          actionRef={actionRef}
          columns={columns}
          request={requestItems}
          params={{ relation: approvals ? 'ACTIONABLE' : 'MY_REQUESTS' }}
          headerTitle={t(approvals ? 'nav.approvals' : 'nav.requests')}
          search={{ labelWidth: 'auto', defaultCollapsed: false }}
          options={{
            reload: true,
            density: true,
            setting: true,
            fullScreen: true,
          }}
          toolBarRender={() =>
            approvals
              ? []
              : [
                  <Button
                    key="create"
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!availableOperations.length}
                    onClick={() => {
                      form.resetFields();
                      form.setFieldValue(
                        'operationType',
                        availableOperations[0] ?? 'CONSUME',
                      );
                      setPrefillSamples([]);
                      setOpen(true);
                    }}
                  >
                    {t('requests.create')}
                  </Button>,
                ]
          }
          tableLayout="fixed"
          scroll={{ x: tableWidth }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          cardBordered
        />
      )}
      <Drawer
        title={selected?.requestNo}
        open={!!selected}
        onClose={() => setSelected(undefined)}
        width={760}
        extra={
          !approvals && selected?.status === 'DRAFT' ? (
            <Button
              icon={<SendOutlined />}
              onClick={() =>
                void call(selected.id, 'submit', { version: selected.version })
              }
            >
              {t('requests.submit')}
            </Button>
          ) : undefined
        }
      >
        <Space orientation="vertical" size={24} style={{ width: '100%' }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label={t('requests.status')}>
              {selected ? requestStatusLabel(selected.status) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.operation')}>
              {selected ? operationLabel(selected.operationType) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('requests.reason')} span={2}>
              {selected?.reason || '-'}
            </Descriptions.Item>
          </Descriptions>
          <section>
            <Typography.Title level={5} style={{ margin: '0 0 12px' }}>
              {t('requests.items')}
            </Typography.Title>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              tableLayout="fixed"
              scroll={{ x: 720 }}
              dataSource={selected?.items ?? []}
              columns={[
                {
                  title: t('samples.cellLine'),
                  width: 145,
                  ellipsis: true,
                  render: (_, row) => row.sample.cellLineCode ?? '-',
                },
                {
                  title: t('samples.material'),
                  width: 145,
                  ellipsis: true,
                  render: (_, row) => row.sample.materialName ?? '-',
                },
                {
                  title: t('samples.passage'),
                  width: 85,
                  render: (_, row) => row.sample.passage ?? '-',
                },
                {
                  title: t('requests.status'),
                  width: 105,
                  render: (_, row) => sampleStatusLabel(row.sample.status),
                },
                {
                  title: t('samples.position'),
                  width: 220,
                  ellipsis: true,
                  render: (_, row) => positionPath(row.fromLocationId),
                },
              ]}
            />
          </section>
          <section>
            <Typography.Title level={5} style={{ margin: '0 0 12px' }}>
              {t('requests.history')}
            </Typography.Title>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              tableLayout="fixed"
              scroll={{ x: 680 }}
              dataSource={selected?.actions ?? []}
              columns={[
                {
                  title: t('requests.time'),
                  dataIndex: 'createdAt',
                  width: 170,
                  render: (value: string) => formatTenantDateTime(value),
                },
                {
                  title: t('requests.action'),
                  width: 120,
                  render: (_, row) => requestActionLabel(row.action),
                },
                {
                  title: t('requests.transition'),
                  width: 190,
                  render: (_, row) =>
                    `${requestStatusLabel(row.fromStatus)} → ${requestStatusLabel(row.toStatus)}`,
                },
                {
                  title: t('requests.comment'),
                  dataIndex: 'comment',
                  ellipsis: true,
                },
              ]}
            />
          </section>
        </Space>
      </Drawer>
      <Modal
        title={t('requests.createTitle')}
        open={open}
        width={920}
        onCancel={() => {
          setOpen(false);
          setPrefillSamples([]);
          onEmbeddedClose?.();
        }}
        onOk={() => void form.submit()}
        okText={t('requests.submitApplication')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: previewBlockerCount > 0 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void create(values)}
        >
          <Form.Item
            name="operationType"
            label={t('requests.operation')}
            initialValue="CONSUME"
            rules={[{ required: true }]}
          >
            <Select
              options={availableOperations.map((value) => ({
                value,
                label: operationLabel(value),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="title"
            label={t('requests.title')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="reason" label={t('requests.reason')}>
            <AutoComplete
              allowClear
              options={[
                { value: t('requests.reasonOptions.experiment') },
                { value: t('requests.reasonOptions.recovery') },
                { value: t('requests.reasonOptions.quality') },
                { value: t('requests.reasonOptions.collaboration') },
                { value: t('requests.reasonOptions.other') },
              ]}
              placeholder={t('requests.reasonPlaceholder')}
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>
          {!prefillSamples.length && (
            <Form.Item
              name="sampleRefs"
              label={t('requests.batchItems')}
              extra={t('requests.batchItemsHint')}
              rules={[{ required: true }]}
            >
              <Input.TextArea rows={5} placeholder="uuid:1\nuuid:2" />
            </Form.Item>
          )}
          {['MOVE', 'RETURN'].includes(operationTypeValue ?? '') && (
            <Form.Item
              name="toLocationId"
              label={t('requests.targetPosition')}
              rules={[
                { required: true, message: t('requests.targetRequired') },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={positions.map((position) => ({
                  value: position.id,
                  label: position.displayPath ?? position.path,
                }))}
              />
            </Form.Item>
          )}
          {previewRows.length > 0 && (
            <>
              <Alert
                type={
                  previewBlockerCount
                    ? 'error'
                    : ['MOVE', 'RETURN'].includes(operationTypeValue ?? '')
                      ? 'warning'
                      : 'info'
                }
                showIcon
                message={
                  previewBlockerCount
                    ? t('requests.localBlockers', {
                        count: previewBlockerCount,
                      })
                    : t('requests.previewHint', {
                        count: previewRows.length,
                        operation: operationLabel(
                          operationTypeValue ?? 'CONSUME',
                        ),
                      })
                }
                style={{ marginBottom: 12 }}
              />
              <Table
                size="small"
                rowKey="key"
                pagination={false}
                dataSource={previewRows}
                tableLayout="fixed"
                scroll={{ x: prefillSamples.length ? 760 : 700 }}
                columns={
                  prefillSamples.length
                    ? [
                        {
                          title: t('samples.cellLine'),
                          dataIndex: 'cellLine',
                          width: 145,
                          ellipsis: true,
                        },
                        {
                          title: t('samples.material'),
                          dataIndex: 'material',
                          width: 145,
                          ellipsis: true,
                        },
                        {
                          title: t('samples.passage'),
                          dataIndex: 'passage',
                          width: 85,
                        },
                        {
                          title: t('requests.status'),
                          dataIndex: 'status',
                          width: 105,
                          render: (value: string) => sampleStatusLabel(value),
                        },
                        {
                          title: t('samples.position'),
                          dataIndex: 'storagePosition',
                          width: 210,
                          ellipsis: true,
                        },
                        {
                          title: t('requests.localCheck'),
                          width: 120,
                          render: (_, row) =>
                            row.issues.length ? (
                              <Space wrap>
                                {row.issues.map((issue) => (
                                  <Tag color="red" key={issue}>
                                    {issue}
                                  </Tag>
                                ))}
                              </Space>
                            ) : (
                              <Tag color="green">{t('samples.executable')}</Tag>
                            ),
                        },
                      ]
                    : [
                        {
                          title: t('requests.sampleNo'),
                          dataIndex: 'sampleNo',
                          width: 180,
                          ellipsis: true,
                        },
                        {
                          title: t('requests.sampleVersion'),
                          dataIndex: 'version',
                          width: 100,
                        },
                        {
                          title: t('requests.status'),
                          dataIndex: 'status',
                          width: 120,
                        },
                        {
                          title: t('requests.expectedStatus'),
                          dataIndex: 'expectedStatus',
                          width: 130,
                        },
                        {
                          title: t('requests.fromLocation'),
                          render: (_, row) =>
                            positionPath(row.storagePositionId),
                          width: 180,
                          ellipsis: true,
                        },
                        {
                          title: t('requests.localCheck'),
                          width: 130,
                          render: (_, row) =>
                            row.issues.length ? (
                              <Space wrap>
                                {row.issues.map((issue) => (
                                  <Tag color="red" key={issue}>
                                    {issue}
                                  </Tag>
                                ))}
                              </Space>
                            ) : (
                              <Tag color="green">{t('samples.executable')}</Tag>
                            ),
                        },
                      ]
                }
              />
            </>
          )}
        </Form>
      </Modal>
      <Modal
        title={t('requests.executionPreview')}
        open={!!execution}
        okText={t('requests.execute')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: !execution?.preview.executable }}
        onCancel={() => setExecution(undefined)}
        onOk={() => void confirmExecution()}
        width={820}
      >
        {execution && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type={execution.preview.executable ? 'info' : 'error'}
              showIcon
              message={
                execution.preview.executable
                  ? t('requests.executionReady', {
                      count: execution.preview.items.length,
                    })
                  : t('requests.executionBlocked', {
                      count: execution.preview.issues.length,
                    })
              }
            />
            <Input.TextArea
              rows={3}
              value={execution.reason}
              onChange={(event) =>
                setExecution((current) =>
                  current
                    ? { ...current, reason: event.target.value }
                    : current,
                )
              }
            />
            <Table
              size="small"
              rowKey="itemId"
              pagination={false}
              dataSource={execution.preview.items}
              columns={[
                { title: t('requests.sampleNo'), dataIndex: 'sampleNo' },
                {
                  title: t('samples.label'),
                  render: (_, row) => row.sampleLabel ?? '-',
                },
                {
                  title: t('requests.statusChange'),
                  render: (_, row) => `${row.fromStatus} → ${row.toStatus}`,
                },
                {
                  title: t('requests.locationChange'),
                  render: (_, row) =>
                    `${positionPath(row.fromLocationId)} → ${positionPath(row.toLocationId)}`,
                },
                {
                  title: t('requests.failures'),
                  render: (_, row) =>
                    row.issues.length
                      ? row.issues.map((issue) => issueText(issue)).join(', ')
                      : '-',
                },
              ]}
            />
            {execution.preview.issues.length > 0 && (
              <Table
                size="small"
                rowKey={(row) => `${row.sampleNo ?? 'request'}-${row.code}`}
                pagination={false}
                dataSource={execution.preview.issues}
                columns={[
                  { title: t('requests.sampleNo'), dataIndex: 'sampleNo' },
                  {
                    title: t('requests.failureCode'),
                    render: (_, row) => issueText(row),
                  },
                ]}
              />
            )}
          </Space>
        )}
      </Modal>
    </section>
  );
}
